import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'

import CoreStoreModule from '@revas/kadra-library/src/revas/vue/vuex/CoreStoreModule'
import AppStoreModule from '@revas/kadra-library/src/core/app/vue/vuex/AppStoreModule'
import HumansStoreModuleContainer from './HumansStoreModuleContainer'

import { Client as RevasClient } from '@revas/ts-api'
import { safeAxiosPost } from '../../utils/safe-post'
import { AxiosInstance } from 'axios'
import VueI18n from 'vue-i18n'

import StoreActionLock from '@revas/kadra-library/src/core/app/vue/vuex/StoreActionLock'
import AppError from '@revas/kadra-library/src/core/app/vue/vuex/AppError'

import PaginationState from './models/pagination/PaginationState'

import ListResourcesActionPayload from './models/resource/ListResourcesActionPayload'
import ListResourcesMutationPayload from './models/resource/ListResourcesMutationPayload'
import ListResourcesPagination, { ResourceContact } from './models/pagination/ListResourcesPagination'

import { AvailabilityPeriod, Contact, Resource, WorkSchedule } from '@/models'
import GetResourceMutationPayload from './models/resource/GetResourceMutationPayload'

import SearchContactMutationPayload from './models/contact/SearchContactMutationPayload'
import SearchContactActionPayload from './models/contact/SearchContactActionPayload'
import SelectSearchedContactActionPayload from './models/contact/SelectSearchedContactActionPayload'
import SelectSearchedContactMutationPayload from './models/contact/SelectSearchedContactMutationPayload'
import SelectContactActionPayload from './models/contact/SelectContactActionPayload'
import SelectContactMutationPayload from './models/contact/SelectContactMutationPayload'

import Availability from './models/Availability'
import GetAvailabilityActionPayload from './models/availability/GetAvailabilityActionPayload'
import GetAvailabilityMutationPayload from './models/availability/GetAvailabilityMutationPayload'
import SaveAvailabilityActionPayload from './models/availability/SaveAvailabilityActionPayload'
import SaveAvailabilityMutationPayload from './models/availability/SaveAvailabilityMutationPayload'
import Allocation from './models/Allocation'
import ListAllocationsPagination from './models/allocation/ListAllocationsPagination'
import ListAllocationsActionPayload from './models/allocation/ListAllocationsActionPayload'
import ListAllocationsMutationPayload from './models/allocation/ListAllocationsMutationPayload'
import Page from './models/pagination/Page'

import AllocationCalendar from './models/AllocationCalendar'
import GetAllocationCalendarActionPayload from './models/allocation/GetAllocationCalendarActionPayload'
import GetAllocationCalendarMutationPayload from './models/allocation/GetAllocationCalendarMutationPayload'
import { addDays, createUtcIsoDate, substractDays } from './helper'

import ListInfiniteResourcePagination from './models/pagination/ListInfiniteResourcePagination'
import AllocationCalendarWithContact from './models/AllocationCalendarWithContact'
import GetAllocationCalendarsMutationPayload from './models/allocation/GetAllocationCalendarsMutationPayload'
import GetAllocationCalendarsActionPayload from './models/allocation/GetAllocationCalendarsActionPayload'
import AllocationDay from './models/AllocationDay'
import { ApplyPeriodCostCorrectionActionPayload, DeleteWorkScheduleActionPayload, GetWorkScheduleActionPayload, GetWorkScheduleMutationPayload, SaveWorkScheduleActionPayload, SaveWorkScheduleMutationPayload } from './models/workSchedules'
import { Metadata } from '@revas/ts-api/src/resources/content'
import Address from './models/Address'

import cloneDeep from 'lodash.clonedeep'
import ListAvailabilityPeriodsPagination from './models/periods/ListAvailabilityPeriodsPagination'

export interface HumansStore {
  currentResource: Resource | null;
  asyncGetResource (payload: { resourceId: string }): Promise<void>;

  currentAvailabilityPeriod: AvailabilityPeriod | null;
  isCreatingAvailabilityPeriod: boolean;
  asyncSaveAvailabilityPeriod (payload: { resourceId: string; availabilityPeriod: AvailabilityPeriod }): Promise<void>;
  asyncGetAvailabilityPeriod (payload: { resourceId: string; availabilityPeriodId: string }): Promise<void>;
  asyncDeleteAvailabilityPeriod (payload: { resourceId: string; availabilityPeriod: AvailabilityPeriod }): Promise<void>;

  currentAvailabilityPeriodsItems: AvailabilityPeriod[] | null;
  currentAvailabilityPeriodsPage: number;
  currentAvailabilityPeriodsHasNextPage: boolean;
  asyncListAvailabilityPeriods (payload: { resourceId: string; page: Page<AvailabilityPeriod> }): Promise<void>;
}

@Module({
  name: 'humans',
  stateFactory: true,
  namespaced: true
})
export default class HumansStoreModule extends VuexModule implements HumansStore {
  private _coreStoreModule: CoreStoreModule | null = null
  private _appStoreModule: AppStoreModule | null = null
  private _revasClient: RevasClient | null = null
  private _axios: AxiosInstance | null = null
  private _i18n: VueI18n | null = null

  @Mutation
  inject (container: HumansStoreModuleContainer) {
    this._coreStoreModule = container.coreStoreModule
    this._appStoreModule = container.appStoreModule
    this._revasClient = container.revasClient
    this._axios = container.axios
    this._i18n = container.i18n
  }

  //
  //
  // Get Resource
  private _currentResource: Resource | null = null

  public get currentResource (): Resource | null {
    return this._currentResource
  }

  private _currentWorkSchedules: WorkSchedule[] | null = null

  public get currentWorkSchedules (): WorkSchedule[] | null {
    return this._currentWorkSchedules
  }

  @Mutation
  private setResource (payload: { resource: Resource }): void {
    this._currentResource = payload.resource
  }

  @Mutation
  private getResourceSuccess (payload: GetResourceMutationPayload) {
    this._currentResource = payload.resource
    this._currentWorkSchedules = payload.workSchedules
    if (this._currentResource.metadata !== undefined) {
      const contactName = this._currentResource.metadata.find((m: Metadata) => {
        return m.key === 'contacts.contact.name'
      })
      if (contactName !== undefined) {
        this._selectedContact = {
          id: this._currentResource.contactId,
          name: contactName.value,
          birthDate: '',
          birthPlace: '',
          email: '',
          internalCode: '',
          legalAddress: {
            city: '',
            country: '',
            details: '',
            notes: '',
            postalCode: '',
            province: '',
            street: ''
          },
          mailingAddress: {
            city: '',
            country: '',
            details: '',
            notes: '',
            postalCode: '',
            province: '',
            street: ''
          },
          legalEmail: '',
          locked: false,
          metadata: [],
          phone: '',
          taxCode: '',
          type: 'natural',
          groupId: '',
          vatCode: '',
          legalContactId: '',
          memberId: '',
          notes: ''
        }
      } else {
        this._selectedContact = null
      }
    }
  }

  @Action({ rawError: true })
  public async asyncGetResource (payload: { resourceId: string }) {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._coreStoreModule === null || this._revasClient === null || this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    if (payload.resourceId === '-') {
      const resource: Resource = {
        id: '',
        organizationId: '',
        contactId: '',
        effectiveFrom: '',
        effectiveTo: '',
        metadata: []
      }
      const mutationPayload = new GetResourceMutationPayload(resource, [])
      this.getResourceSuccess(mutationPayload)
    } else {
      const [getResourceRes, getResourceErr] = await safeAxiosPost<any>(this._axios, '/humans/humans.GetResource/', {
        resourceId: payload.resourceId
      })

      if (getResourceErr !== null) {
        const errorPayload = new AppError()
        errorPayload.error = {
          name: getResourceErr.message,
          message: getResourceErr.message
        }
        this._appStoreModule.asyncPushError(errorPayload)
        this._appStoreModule.asyncUnlock(lock)
        return
      }

      const resource: Resource = getResourceRes.resource

      let pageToken = 'first-call'
      const workSchedules: WorkSchedule[] = []

      while (pageToken !== '') {
        const [listWorkSchedulesRes, listWorkSchedulesErr] = await safeAxiosPost<any>(this._axios, '/humans/humans.ListWorkSchedules/', {
          contactId: resource.contactId,
          pageSize: 2,
          pageToken: pageToken === 'first-call' ? '' : pageToken,
          search: ''
        })

        if (listWorkSchedulesErr !== null) {
          const errorPayload = new AppError()
          errorPayload.error = {
            name: listWorkSchedulesErr.message,
            message: listWorkSchedulesErr.message
          }
          this._appStoreModule.asyncPushError(errorPayload)
          this._appStoreModule.asyncUnlock(lock)
          return
        }
        for (let index = 0; index < listWorkSchedulesRes.workSchedules.length; index++) {
          const ws: WorkSchedule = listWorkSchedulesRes.workSchedules[index]
          ws.effectiveTo = createUtcIsoDate(substractDays(new Date(ws.effectiveTo), 1))
        }
        workSchedules.push(...listWorkSchedulesRes.workSchedules)
        pageToken = listWorkSchedulesRes.nextPageToken
      }

      const mutationPayload = new GetResourceMutationPayload(resource, workSchedules)
      this.getResourceSuccess(mutationPayload)
    }
    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // Get WorkSchedule
  public get isCreatingWorkSchedule (): boolean {
    if (this._currentWorkSchedule === null || this._currentWorkSchedule.id === '') {
      return true
    } else {
      return false
    }
  }

  @Mutation
  private getWorkScheduleSuccess (payload: GetWorkScheduleMutationPayload) {
    this._currentWorkSchedule = payload.workSchedule
    if (this._currentWorkSchedule.metadata !== undefined) {
      const contactName = this._currentWorkSchedule.metadata.find((m: Metadata) => {
        return m.key === 'contacts.contact.name'
      })
      if (contactName !== undefined) {
        this._selectedContact = {
          id: this._currentWorkSchedule.contactId,
          name: contactName.value,
          birthDate: '',
          birthPlace: '',
          email: '',
          internalCode: '',
          legalAddress: {
            city: '',
            country: '',
            details: '',
            notes: '',
            postalCode: '',
            province: '',
            street: ''
          },
          mailingAddress: {
            city: '',
            country: '',
            details: '',
            notes: '',
            postalCode: '',
            province: '',
            street: ''
          },
          legalEmail: '',
          locked: false,
          metadata: [],
          phone: '',
          taxCode: '',
          type: 'natural',
          groupId: '',
          vatCode: '',
          legalContactId: '',
          memberId: '',
          notes: ''
        }
      } else {
        this._selectedContact = null
      }
    }
  }

  @Action({ rawError: true })
  public async asyncGetWorkSchedule (payload: GetWorkScheduleActionPayload) {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._coreStoreModule === null || this._revasClient === null || this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    if (payload.workScheduleId === '-') {
      const workSchedule: WorkSchedule = {
        id: '',
        organizationId: '',
        contactId: '',
        effectiveFrom: '',
        effectiveTo: '',
        hourlyCost: 0,
        hourlyPrice: 0,
        weeklyDurations: [0, 0, 0, 0, 0, 0, 0],
        notes: ''
      }
      this.getWorkScheduleSuccess({
        workSchedule: workSchedule
      })
    } else {
      const [getResourceRes, getResourceErr] = await safeAxiosPost<any>(this._axios, '/humans/humans.GetResource/', {
        resourceId: payload.resourceId
      })

      if (getResourceErr !== null) {
        const errorPayload = new AppError()
        errorPayload.error = {
          name: getResourceErr.message,
          message: getResourceErr.message
        }
        this._appStoreModule.asyncPushError(errorPayload)
        this._appStoreModule.asyncUnlock(lock)
        return
      }

      const resource: Resource = getResourceRes.resource

      const [getWorkScheduleRes, getWorkScheduleErr] = await safeAxiosPost<any>(this._axios, '/humans/humans.GetWorkSchedule/', {
        workScheduleId: payload.workScheduleId,
        contactId: resource.contactId
      })

      if (getWorkScheduleErr !== null) {
        const errorPayload = new AppError()
        errorPayload.error = {
          name: getWorkScheduleErr.message,
          message: getWorkScheduleErr.message
        }
        this._appStoreModule.asyncPushError(errorPayload)
        this._appStoreModule.asyncUnlock(lock)
        return
      }
      const workSchedule: WorkSchedule = getWorkScheduleRes.workSchedule
      workSchedule.effectiveTo = createUtcIsoDate(substractDays(new Date(workSchedule.effectiveTo), 1))
      this.getWorkScheduleSuccess({
        workSchedule: getWorkScheduleRes.workSchedule
      })
    }
    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // Save Work Schedule
  private _currentWorkSchedule: WorkSchedule | null = null

  public get currentWorkSchedule (): WorkSchedule | null {
    return this._currentWorkSchedule
  }

  @Mutation
  private saveWorkScheduleSuccess (payload: SaveWorkScheduleMutationPayload) {
    this._currentWorkSchedule = payload.workSchedule
  }

  @Action({ rawError: true })
  public async asyncSaveWorkSchedule (payload: SaveWorkScheduleActionPayload) {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._coreStoreModule === null || this._revasClient === null || this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    if (this._selectedContact === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    let res = null
    let err = null
    payload.workSchedule.contactId = this._selectedContact.id
    const workSchedule = cloneDeep(payload.workSchedule)
    workSchedule.effectiveTo = createUtcIsoDate(addDays(new Date(workSchedule.effectiveTo), 1))

    if (payload.workSchedule.id === '') {
      [res, err] = await safeAxiosPost<any>(this._axios, '/humans/humans.CreateWorkSchedule/', {
        workSchedule: workSchedule,
        contactId: this._selectedContact.id
      })
    } else {
      [res, err] = await safeAxiosPost<any>(this._axios, '/humans/humans.UpdateWorkSchedule/', {
        workScheduleId: workSchedule.id,
        workSchedule: workSchedule,
        contactId: this._selectedContact.id
      })
    }

    if (err !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: err.message,
        message: err.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this._appStoreModule.asyncUnlock(lock)
      return
    }

    this.saveWorkScheduleSuccess({
      workSchedule: res.workSchedule
    })
    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // Select Contact
  private _selectedContact: Contact | null = null
  public get selectedContact (): Contact | null {
    return this._selectedContact
  }

  @Mutation
  private selectContactSuccess (payload: SelectContactMutationPayload) {
    this._selectedContact = payload.contact
  }

  @Action
  selectContact (payload: SelectContactActionPayload) {
    const mutationPayload = new SelectContactMutationPayload(payload.contact)
    this.selectContactSuccess(mutationPayload)
  }

  //
  //
  // Reset Selected Contact
  @Mutation
  private resetSelectedContactSuccess () {
    this._selectedContact = null
  }

  @Action({ rawError: true })
  public resetSelectedContact () {
    this.resetSelectedContactSuccess()
  }

  //
  //
  // List Resources
  private _currentResources: PaginationState<ResourceContact> = new ListResourcesPagination(this._revasClient!, 0, '-')

  public get currentResourcesPage (): number {
    return this._currentResources.getCurrentPage().number
  }

  public get currentResourcesItems (): ResourceContact[] | null {
    return this._currentResources.getCurrentPage().items
  }

  public get currentResourcesHasNextPage (): boolean {
    return this._currentResources.hasNextPage()
  }

  @Mutation
  private listResourcesSuccess (payload: ListResourcesMutationPayload): void {
    this._currentResources = payload.pagination
    this._selectedContact = null
  }

  @Action({ rawError: true })
  async asyncListResources (payload: ListResourcesActionPayload): Promise<void> {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._revasClient === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    let pagination: PaginationState<ResourceContact>
    if (this._currentResources.search === payload.page.search) {
      pagination = this._currentResources.clone()
    } else {
      const pageSize = 15
      pagination = new ListResourcesPagination(this._revasClient, pageSize, payload.page.search)
    }
    try {
      await pagination.asyncChangeCurrentPage(payload.page.number)
      const mutationPayload = new ListResourcesMutationPayload(pagination)
      this.listResourcesSuccess(mutationPayload)
    } catch (error) {
      const errorPayload = new AppError()
      errorPayload.error = error
      this._appStoreModule.asyncPushError(errorPayload)
    }
    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // Search Contact
  private _contactsSearchedByName: Contact[] | null = null

  get contactsSearchedByName (): Contact[] | null {
    return this._contactsSearchedByName
  }

  @Mutation
  searchContactsByNameSuccess (payload: SearchContactMutationPayload) {
    this._contactsSearchedByName = payload.contacts
  }

  @Action({ rawError: true })
  public async asyncSearchContact (payload: SearchContactActionPayload) {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._revasClient === null || this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    let search = ''
    if (payload.search !== '') {
      search = `eq[name]=${payload.search}`
    } else {
      search = ''
    }

    const [res, err] = await safeAxiosPost<any>(this._axios, '/contacts/contacts.ListContacts/', {
      pageSize: 5,
      pageToken: '',
      search: search
    })

    if (err !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: err.message,
        message: err.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const mutatationPayload = new SearchContactMutationPayload(res.contacts)
    this.searchContactsByNameSuccess(mutatationPayload)
  }

  //
  //
  // Search Resource
  private _resourcesSearchedByName: ResourceContact[] | null = null

  get resourcesSearchedByName (): ResourceContact[] | null {
    return this._resourcesSearchedByName
  }

  @Mutation
  searchResourcesByNameSuccess (payload: { resourceContacts: ResourceContact[] }) {
    this._resourcesSearchedByName = payload.resourceContacts
  }

  @Action({ rawError: true })
  public async asyncSearchResources (payload: SearchContactActionPayload) {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._revasClient === null || this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    let search = ''
    if (payload.search !== '') {
      search = `eq[name]=${payload.search}`
    } else {
      search = ''
    }
    try {
      const [res, err] = await safeAxiosPost<any>(this._axios, '/contacts/contacts.ListContacts/', {
        pageSize: 5,
        pageToken: '',
        search: search
      })

      if (err !== null) {
        const errorPayload = new AppError()
        errorPayload.error = {
          name: err.message,
          message: err.message
        }
        this._appStoreModule.asyncPushError(errorPayload)
        return
      }

      const contacts: Contact[] = res.contacts
      const resourceContacts: ResourceContact[] = []
      for (let index = 0; index < contacts.length; index++) {
        const contact = contacts[index]
        const [listResourcesRes, listResourcesErr] = await safeAxiosPost<any>(this._axios, '/humans/humans.ListResources/', {
          pageSize: 1,
          pageToken: '',
          search: payload.resourceSearch === '' ? `eq[contactId]=${contact.id}` : `eq[contactId]=${contact.id}&${payload.resourceSearch}`
        })

        if (listResourcesErr === null) {
          if (listResourcesRes.resources.length > 0 && listResourcesRes.resources[0].contactId === contact.id) {
            const resourceContact: ResourceContact = {
              id: listResourcesRes.resources[0].id,
              contactId: contact.id,
              effectiveFrom: listResourcesRes.resources[0].effectiveFrom,
              effectiveTo: listResourcesRes.resources[0].effectiveTo,
              email: contact.email,
              name: contact.name,
              phone: contact.phone
            }
            resourceContacts.push(resourceContact)
          }
        }
      }

      this.searchResourcesByNameSuccess({ resourceContacts: resourceContacts })
    } catch (error) {
      this._appStoreModule.asyncPanic()
    }
  }

  //
  //
  // Select Search Contact
  private _currentSelectedSearchContact: Contact | null = null
  public get currentSelectedSearchContact (): Contact | null {
    return this._currentSelectedSearchContact
  }

  @Mutation
  private selectSearchContactSuccess (payload: SelectSearchedContactMutationPayload): void {
    this._currentSelectedSearchContact = payload.contact
  }

  @Action({ rawError: true })
  public selectSearchedContact (payload: SelectSearchedContactActionPayload) {
    const mutationPayload = new SelectSearchedContactMutationPayload(payload.contact)
    this.selectSearchContactSuccess(mutationPayload)
  }

  //
  //
  // Reset Contact Search Filter

  @Mutation
  private resetContactSearchFilterSuccess (): void {
    this._currentSelectedSearchContact = null
  }

  @Action({ rawError: true })
  public resetContactSearchFilter () {
    this.resetContactSearchFilterSuccess()
  }

  //
  //
  // Delete Work Schedule

  private _isWorkScheduleDeleted = false

  public get isWorkScheduleDeleted (): boolean {
    return this._isWorkScheduleDeleted
  }

  @Mutation
  private deleteWorkScheduleSuccess () {
    this._isWorkScheduleDeleted = true
    this._currentResources = new ListResourcesPagination(this._revasClient!, 0, '-')
  }

  @Action({ rawError: true })
  public async asyncDeleteWorkSchedule (payload: DeleteWorkScheduleActionPayload) {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._coreStoreModule === null || this._revasClient === null || this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    if (this._selectedContact === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    const [res, err] = await safeAxiosPost<any>(this._axios, '/humans/humans.DeleteWorkSchedule/', {
      workScheduleId: payload.workScheduleId,
      contactId: this._selectedContact.id
    })

    if (err !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: err.message,
        message: err.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this._appStoreModule.asyncUnlock(lock)
      return
    }

    this.deleteWorkScheduleSuccess()
    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // Get Availability
  private _currentAvailability: Availability | null = null
  private _currentAvailabilityContact: Contact | null = null

  public get currentAvailability (): Availability | null {
    return this._currentAvailability
  }

  public get currentAvailabilityContact (): Contact | null {
    return this._currentAvailabilityContact
  }

  public get isCreatingAvailability () {
    if (this._currentAvailability === null || this._currentAvailability.id === '') {
      return true
    } else {
      return false
    }
  }

  @Mutation
  private getAvailabilitySuccess (payload: GetAvailabilityMutationPayload) {
    this._currentAvailability = payload.availability
    this._currentAvailabilityContact = payload.contact
  }

  @Action({ rawError: true })
  public async asyncGetAvailability (payload: GetAvailabilityActionPayload) {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._coreStoreModule === null || this._revasClient === null || this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    if (payload.availabilityId === '-') {
      const availability = new Availability()
      const newContact: Contact = {
        id: '',
        name: '',
        birthDate: '',
        birthPlace: '',
        email: '',
        internalCode: '',
        legalAddress: {
          city: '',
          country: '',
          details: '',
          notes: '',
          postalCode: '',
          province: '',
          street: ''
        },
        mailingAddress: {
          city: '',
          country: '',
          details: '',
          notes: '',
          postalCode: '',
          province: '',
          street: ''
        },
        legalEmail: '',
        locked: false,
        metadata: [],
        phone: '',
        taxCode: '',
        type: 'natural',
        groupId: '',
        vatCode: '',
        legalContactId: '',
        memberId: '',
        notes: ''
      }
      const mutationPayload = new GetAvailabilityMutationPayload(availability, newContact, [])
      this.getAvailabilitySuccess(mutationPayload)
    } else {
      const [getResourceResponse, getResourceErr] = await safeAxiosPost<any>(this._axios, '/humans/humans.GetResource/', {
        resourceId: payload.resourceId
      })

      if (getResourceErr !== null) {
        const errorPayload = new AppError()
        errorPayload.error = {
          name: getResourceErr.message,
          message: getResourceErr.message
        }
        this._appStoreModule.asyncPushError(errorPayload)
        this._appStoreModule.asyncUnlock(lock)
        return
      }
      const resource: Resource = getResourceResponse.resource

      const [getContactResponse, getContactErr] = await safeAxiosPost<any>(this._axios, '/contacts/contacts.GetContact/', {
        contactId: resource.contactId
      })

      if (getContactErr !== null) {
        const errorPayload = new AppError()
        errorPayload.error = {
          name: getContactErr.message,
          message: getContactErr.message
        }
        this._appStoreModule.asyncPushError(errorPayload)
        this._appStoreModule.asyncUnlock(lock)
        return
      }
      const contact = getContactResponse.contact

      const [getAvailabilityRes, getAvailabilityErr] = await safeAxiosPost<any>(this._axios, '/allocations/allocations.GetAvailability/', {
        availabilityId: payload.availabilityId,
        contactId: resource.contactId
      })

      if (getAvailabilityErr !== null) {
        const errorPayload = new AppError()
        errorPayload.error = {
          name: getAvailabilityErr.message,
          message: getAvailabilityErr.message
        }
        this._appStoreModule.asyncPushError(errorPayload)
        this._appStoreModule.asyncUnlock(lock)
        return
      }
      const availability: Availability = getAvailabilityRes.availability

      const firstPage = new Page<Allocation>()
      firstPage.number = 1
      firstPage.search = `eq[date]=${availability.date}`
      const actionPayload = new ListAllocationsActionPayload(firstPage, contact.id)
      await this.asyncListAllocations(actionPayload)
      let allocations: Allocation[] = []
      if (this.currentAllocationsItems !== null) {
        allocations = this.currentAllocationsItems
      }

      const mutationPayload = new GetAvailabilityMutationPayload(availability, contact, allocations)
      this.getAvailabilitySuccess(mutationPayload)
    }
    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // Save Availability
  @Mutation
  private saveAvailabilitySuccess (payload: SaveAvailabilityMutationPayload) {
    this._currentAvailability = payload.availability
  }

  @Action({ rawError: true })
  public async asyncSaveAvailability (payload: SaveAvailabilityActionPayload) {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._coreStoreModule === null || this._revasClient === null || this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    let res = null
    let err = null

    if (payload.availability.id === '') {
      [res, err] = await safeAxiosPost<any>(this._axios, '/allocations/allocations.CreateAvailability/', {
        contactId: payload.availability.contactId,
        availability: payload.availability
      })
    } else {
      [res, err] = await safeAxiosPost<any>(this._axios, '/allocations/allocations.UpdateAvailability/', {
        contactId: payload.availability.contactId,
        availabilityId: payload.availability.id,
        availability: payload.availability
      })
    }

    if (err !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: err.message,
        message: err.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this._appStoreModule.asyncUnlock(lock)
      return
    }

    const mutationPayload = new SaveAvailabilityMutationPayload(res.availability)
    this.saveAvailabilitySuccess(mutationPayload)

    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // List Allocations
  private _currentAllocations: PaginationState<Allocation> = new ListAllocationsPagination(this._revasClient!, 0, '-', '')

  public get currentAllocationsPage (): number {
    return this._currentAllocations.getCurrentPage().number
  }

  public get currentAllocationsItems (): Allocation[] | null {
    return this._currentAllocations.getCurrentPage().items
  }

  public get currentAllocationsHasNextPage (): boolean {
    return this._currentAllocations.hasNextPage()
  }

  @Mutation
  private listAllocationsSuccess (payload: ListAllocationsMutationPayload): void {
    this._currentAllocations = payload.pagination
  }

  @Action({ rawError: true })
  async asyncListAllocations (payload: ListAllocationsActionPayload): Promise<void> {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._revasClient === null || this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    let pagination: PaginationState<Allocation>
    if (this._currentAllocations.search === payload.page.search) {
      pagination = this._currentAllocations.clone()
    } else {
      const pageSize = 15
      pagination = new ListAllocationsPagination(this._revasClient, pageSize, payload.page.search, payload.contactId)
    }
    try {
      await pagination.asyncChangeCurrentPage(payload.page.number)
      const mutationPayload = new ListAllocationsMutationPayload(pagination)
      this.listAllocationsSuccess(mutationPayload)
    } catch (error) {
      const errorPayload = new AppError()
      errorPayload.error = error
      this._appStoreModule.asyncPushError(errorPayload)
    }
    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // Get Allocation Calendar
  private _currentAllocationCalendar: AllocationCalendar | null = null
  private _currentCalendarContact: Contact | null = null

  public get currentAllocationCalendar (): AllocationCalendar | null {
    return this._currentAllocationCalendar
  }

  public get currentCalendarContact (): Contact | null {
    return this._currentCalendarContact
  }

  @Mutation
  private getAllocationCalendarSuccess (payload: GetAllocationCalendarMutationPayload) {
    this._currentAllocationCalendar = payload.allocationCalendar
    this._currentCalendarContact = payload.contact
  }

  @Action({ rawError: true })
  public async asyncGetAllocationCalendar (payload: GetAllocationCalendarActionPayload) {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._coreStoreModule === null || this._revasClient === null || this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    let contact: Contact = {
      id: '',
      name: '',
      birthDate: '',
      birthPlace: '',
      email: '',
      internalCode: '',
      legalAddress: new Address(),
      legalEmail: '',
      locked: false,
      mailingAddress: new Address(),
      metadata: [],
      phone: '',
      taxCode: '',
      type: 'natural',
      groupId: '',
      vatCode: '',
      legalContactId: '',
      memberId: '',
      notes: ''
    }

    let contactId = payload.contactId
    if (contactId === '') {
      const [resourceResponse, resourceErr] = await safeAxiosPost<any>(this._axios, '/humans/humans.GetResource/', {
        resourceId: payload.resourceId
      })

      if (resourceErr !== null) {
        const errorPayload = new AppError()
        errorPayload.error = {
          name: resourceErr.message,
          message: resourceErr.message
        }
        this._appStoreModule.asyncPushError(errorPayload)
        this._appStoreModule.asyncUnlock(lock)
        return
      }
      contactId = resourceResponse.resource.contactId
    }

    const [contactResponse, contactErr] = await safeAxiosPost<any>(this._axios, '/contacts/contacts.GetContact/', {
      contactId: contactId
    })

    if (contactErr !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: contactErr.message,
        message: contactErr.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this._appStoreModule.asyncUnlock(lock)
      return
    }

    contact = contactResponse.contact
    const toDate = createUtcIsoDate(addDays(new Date(payload.to), 1))

    const [getCalendarRes, getCalendarErr] = await safeAxiosPost<any>(this._axios, '/reports/reports.GetAllocationCalendar/', {
      periodicity: payload.periodicity,
      contactId: contactId,
      from: payload.from,
      to: toDate
    })

    if (getCalendarErr !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: getCalendarErr.message,
        message: getCalendarErr.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this._appStoreModule.asyncUnlock(lock)
      return
    }

    const calendar = getCalendarRes.calendar

    const allocationPeriods: AllocationDay[] = []
    for (let i = 0; i < calendar.periods.length; i++) {
      const period = calendar.periods[i]
      const ai: AllocationDay = {
        contactId: period.contactId,
        from: period.from,
        to: createUtcIsoDate(substractDays(new Date(period.to), 1)),
        hourlyCost: period.hourlyCost,
        availabilityDuration: period.availabilityDuration,
        allocationDuration: period.allocationDuration
      }
      allocationPeriods.push(ai)
    }

    const allocationCalendar: AllocationCalendar = {
      contactId: calendar.contactId,
      from: calendar.from,
      periods: allocationPeriods,
      to: createUtcIsoDate(substractDays(new Date(calendar.to), 1))
    }

    const mutationPayload = new GetAllocationCalendarMutationPayload(allocationCalendar, contact)
    this.getAllocationCalendarSuccess(mutationPayload)

    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // Get All Resources Allocation Calendars
  private _currentAllocationCalendars: AllocationCalendarWithContact[] | null = null

  public get currentAllocationCalendars (): AllocationCalendarWithContact[] | null {
    return this._currentAllocationCalendars
  }

  @Mutation
  private getAllocationCalendarsSuccess (payload: GetAllocationCalendarsMutationPayload) {
    this._currentAllocationCalendars = payload.calendars
  }

  @Action({ rawError: true })
  public async asyncGetAllocationCalendars (payload: GetAllocationCalendarsActionPayload) {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._coreStoreModule === null || this._revasClient === null || this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)
    try {
      const pageSize = 15
      const pagination = new ListInfiniteResourcePagination(this._revasClient, pageSize, `ge[effectiveTo]=${createUtcIsoDate(addDays(new Date(), 1))}`)
      await pagination.asyncChangeCurrentPage(1)

      const resources = pagination.getCurrentPage().items
      let calendars: AllocationCalendarWithContact[] = []
      if (resources !== null && resources.length > 0) {
        const promises: Promise<AllocationCalendarWithContact>[] = []
        for (let index = 0; index < resources.length; index++) {
          const resource = resources[index]
          const toDate = createUtcIsoDate(addDays(new Date(payload.to), 1))
          const contactName = resource.metadata.find((m: Metadata) => m.key === 'contacts.contact.name')

          promises.push(new Promise((resolve, reject) => {
            safeAxiosPost<any>(this._axios!, '/reports/reports.GetAllocationCalendar/', {
              periodicity: payload.periodicity,
              contactId: resource.contactId,
              from: payload.from,
              to: toDate
            }).then(([res, err]) => {
              if (err) {
                resolve({
                  contactId: resource.contactId,
                  name: contactName === undefined ? '' : contactName.value,
                  from: payload.from,
                  to: payload.to,
                  periods: [],
                  error: true
                } as AllocationCalendarWithContact)
                return
              }
              resolve({
                contactId: res.calendar.contactId,
                from: res.calendar.from,
                to: createUtcIsoDate(substractDays(new Date(res.calendar.to), 1)),
                periods: res.calendar.periods,
                name: contactName === undefined ? '' : contactName.value,
                error: false
              })
            }).catch((error) => {
              reject(error)
            })
          }))
        }
        const calendarsWithContacts = (await Promise.allSettled(promises)).map((r: PromiseSettledResult<AllocationCalendarWithContact>) => {
          if (r.status === 'fulfilled') {
            return r.value
          } else {
            return {} as AllocationCalendarWithContact
          }
        })
        calendars = calendarsWithContacts
      }

      const mutationPayload = new GetAllocationCalendarsMutationPayload(calendars)
      this.getAllocationCalendarsSuccess(mutationPayload)
    } catch (error) {
      this._appStoreModule.asyncPushError(error)
    }
    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // Apply Period Cost Correction

  private _isCostCorrectionApplied = false

  public get isCostCorrectionApplied (): boolean {
    return this._isCostCorrectionApplied
  }

  @Mutation
  private costCorrectionSuccess () {
    this._isCostCorrectionApplied = true
  }

  @Action({ rawError: true })
  public async asyncApplyPeriodCostCorrection (payload: ApplyPeriodCostCorrectionActionPayload) {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._coreStoreModule === null || this._revasClient === null || this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }
    if (this._selectedContact === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }
    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    payload.costCorrection.to = createUtcIsoDate(addDays(new Date(payload.costCorrection.to), 1))
    const [res, err] = await safeAxiosPost<any>(this._axios, '/allocations/allocations.ApplyPeriodCostCorrection/', {
      contactId: this._selectedContact.id,
      from: payload.costCorrection.from,
      to: payload.costCorrection.to,
      totalPeriodCost: payload.costCorrection.totalPeriodCost
    })

    if (err !== null) {
      console.log('err', err)
      const errorPayload = new AppError()
      errorPayload.error = {
        name: err.message,
        message: err.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this._appStoreModule.asyncUnlock(lock)
      return
    }

    this.costCorrectionSuccess()
    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // Regenerate Allocations
  private _areAllocationsRegenerated = false

  public get areAllocationsRegenerated (): boolean {
    return this._areAllocationsRegenerated
  }

  @Mutation
  private regenerateAllocationsSuccess () {
    this._areAllocationsRegenerated = true
  }

  @Action({ rawError: true })
  public async asyncRegenerateAllocations (payload: { fromDate: string }) {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._coreStoreModule === null || this._revasClient === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    try {
      await this._revasClient.asyncRevasPost('/allocations/allocations.RegenAllocations/', {
        startDate: payload.fromDate
      })
      this.regenerateAllocationsSuccess()
    } catch (error) {
      const errorPayload = new AppError()
      errorPayload.error = error
      this._appStoreModule.asyncPushError(error)
    }
    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // List Availability Periods
  private _currentAvailabilityPeriodsPagination: PaginationState<AvailabilityPeriod> = new ListAvailabilityPeriodsPagination(this._axios!, this._appStoreModule!, '', 0, '-')

  private _availabilityPeriodPaginationResourceId = ''

  public get currentAvailabilityPeriodsItems (): AvailabilityPeriod[] | null {
    return this._currentAvailabilityPeriodsPagination.getCurrentPage().items
  }

  public get currentAvailabilityPeriodsPage (): number {
    return this._currentAvailabilityPeriodsPagination.getCurrentPage().number
  }

  public get currentAvailabilityPeriodsHasNextPage (): boolean {
    return this._currentAvailabilityPeriodsPagination.hasNextPage()
  }

  @Mutation
  private setAvailabilityPeriods (payload: { pagination: PaginationState<AvailabilityPeriod>; resourceId: string }): void {
    this._currentAvailabilityPeriodsPagination = payload.pagination
  }

  @Action({ rawError: true })
  public async asyncListAvailabilityPeriods (payload: { resourceId: string; page: Page<AvailabilityPeriod> }): Promise<void> {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    const [getResourceRes, getResourceErr] = await safeAxiosPost<any>(this._axios, '/humans/humans.GetResource/', {
      resourceId: payload.resourceId
    })

    if (getResourceErr !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: getResourceErr.message,
        message: getResourceErr.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this._appStoreModule.asyncUnlock(lock)
      return
    }

    this.setResource({ resource: getResourceRes.resource })

    let pagination: PaginationState<AvailabilityPeriod>
    if (this._currentAvailabilityPeriodsPagination.search === payload.page.search && this._availabilityPeriodPaginationResourceId === payload.resourceId) {
      pagination = this._currentAvailabilityPeriodsPagination.clone()
    } else {
      const PAGE_SIZE = 15
      pagination = new ListAvailabilityPeriodsPagination(this._axios, this._appStoreModule, getResourceRes.resource.contactId, PAGE_SIZE, payload.page.search)
    }

    try {
      await pagination.asyncChangeCurrentPage(payload.page.number)
      this.setAvailabilityPeriods({ pagination: pagination, resourceId: payload.resourceId })
    } catch (error) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
    }
    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // Save Availability Period
  private _currentAvailabilityPeriod: AvailabilityPeriod | null = null
  get currentAvailabilityPeriod (): AvailabilityPeriod | null {
    return this._currentAvailabilityPeriod
  }

  get isCreatingAvailabilityPeriod (): boolean {
    if (this._currentAvailabilityPeriod === null || this._currentAvailabilityPeriod.id === '') {
      return true
    } else {
      return false
    }
  }

  @Mutation
  private setAvailabilityPeriod (payload: { availabilityPeriod: AvailabilityPeriod }) {
    this._currentAvailabilityPeriod = payload.availabilityPeriod
  }

  @Action({ rawError: true })
  async asyncSaveAvailabilityPeriod (payload: { resourceId: string; availabilityPeriod: AvailabilityPeriod }): Promise<void> {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    const [getResourceRes, getResourceErr] = await safeAxiosPost<any>(this._axios, '/humans/humans.GetResource/', {
      resourceId: payload.resourceId
    })

    if (getResourceErr !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: getResourceErr.message,
        message: getResourceErr.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this._appStoreModule.asyncUnlock(lock)
      return
    }

    this.setResource({ resource: getResourceRes.resource })

    let res = null
    let err = null

    const availabilityPeriod = cloneDeep(payload.availabilityPeriod)
    availabilityPeriod.effectiveTo = createUtcIsoDate(addDays(new Date(availabilityPeriod.effectiveTo), 1))

    if (payload.availabilityPeriod.id === '') {
      [res, err] = await safeAxiosPost<any>(this._axios, '/availabilities/availabilities.CreateAvailabilityPeriod/', {
        contactId: getResourceRes.resource.contactId,
        availabilityPeriod: availabilityPeriod
      })
    } else {
      [res, err] = await safeAxiosPost<any>(this._axios, '/availabilities/availabilities.UpdateAvailabilityPeriod/', {
        contactId: getResourceRes.resource.contactId,
        availabilityPeriodId: availabilityPeriod.id,
        availabilityPeriod: availabilityPeriod
      })
    }

    if (err !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: err.message,
        message: err.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this._appStoreModule.asyncUnlock(lock)
      return
    }

    this.setAvailabilityPeriod({ availabilityPeriod: res.availabilityPeriod })
    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // Get Availability Period
  @Action({ rawError: true })
  async asyncGetAvailabilityPeriod (payload: { resourceId: string; availabilityPeriodId: string }): Promise<void> {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._axios === null || this._i18n === null || this._coreStoreModule === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    const [getResourceRes, getResourceErr] = await safeAxiosPost<any>(this._axios, '/humans/humans.GetResource/', {
      resourceId: payload.resourceId
    })

    if (getResourceErr !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: getResourceErr.message,
        message: getResourceErr.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this._appStoreModule.asyncUnlock(lock)
      return
    }

    this.setResource({ resource: getResourceRes.resource })

    if (payload.availabilityPeriodId === '-') {
      const availabilityPeriod: AvailabilityPeriod = {
        id: '',
        state: 'active',
        organizationId: this._coreStoreModule.activeUserOrganization!.id,
        contactId: getResourceRes.resource.contactId,
        effectiveFrom: createUtcIsoDate(new Date()),
        effectiveTo: createUtcIsoDate(addDays(new Date(), 7)),
        totalCost: 0
      }

      this.setAvailabilityPeriod({ availabilityPeriod: availabilityPeriod })
    } else {
      const [res, err] = await safeAxiosPost<any>(this._axios, '/availabilities/availabilities.GetAvailabilityPeriod/', {
        contactId: getResourceRes.resource.contactId,
        availabilityPeriodId: payload.availabilityPeriodId
      })

      if (err !== null) {
        const errorPayload = new AppError()
        errorPayload.error = {
          name: err.message,
          message: err.message
        }
        this._appStoreModule.asyncPushError(errorPayload)
        this._appStoreModule.asyncUnlock(lock)
        return
      }
      const availabilityPeriod = res.availabilityPeriod as AvailabilityPeriod
      availabilityPeriod.effectiveTo = createUtcIsoDate(substractDays(new Date(availabilityPeriod.effectiveTo), 1))
      this.setAvailabilityPeriod({ availabilityPeriod: res.availabilityPeriod })
    }

    this._appStoreModule.asyncUnlock(lock)
  }

  //
  //
  // Delete Availability Period
  private _hasAvailabilityPeriodBeenDeleted = false
  get hasAvailabilityPeriodBeenDeleted (): boolean {
    return this._hasAvailabilityPeriodBeenDeleted
  }

  @Mutation
  private setAvailabilityPeriodDeletion (payload: { hasAvailabilityPeriodBeenDeleted: boolean }): void {
    this._hasAvailabilityPeriodBeenDeleted = payload.hasAvailabilityPeriodBeenDeleted
    this._currentAvailabilityPeriodsPagination = new ListAvailabilityPeriodsPagination(this._axios!, this._appStoreModule!, '', 0, '-')
  }

  @Action({ rawError: true })
  async asyncDeleteAvailabilityPeriod (payload: { resourceId: string; availabilityPeriod: AvailabilityPeriod }): Promise<void> {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._axios === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    const lock = new StoreActionLock()
    this._appStoreModule.asyncLock(lock)

    const [getResourceRes, getResourceErr] = await safeAxiosPost<any>(this._axios, '/humans/humans.GetResource/', {
      resourceId: payload.resourceId
    })

    if (getResourceErr !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: getResourceErr.message,
        message: getResourceErr.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this._appStoreModule.asyncUnlock(lock)
      return
    }

    this.setResource({ resource: getResourceRes.resource })

    const [res, err] = await safeAxiosPost<any>(this._axios, '/availabilities/availabilities.DeleteAvailabilityPeriod/', {
      contactId: getResourceRes.resource.contactId,
      availabilityPeriodId: payload.availabilityPeriod.id,
      availabilityPeriod: payload.availabilityPeriod
    })

    if (err !== null) {
      const errorPayload = new AppError()
      errorPayload.error = {
        name: err.message,
        message: err.message
      }
      this._appStoreModule.asyncPushError(errorPayload)
      this.setAvailabilityPeriodDeletion({ hasAvailabilityPeriodBeenDeleted: false })
      this._appStoreModule.asyncUnlock(lock)
      return
    }

    this.setAvailabilityPeriodDeletion({ hasAvailabilityPeriodBeenDeleted: true })

    this._appStoreModule.asyncUnlock(lock)
  }
}
